import { ChangeEvent, useState, useCallback, useRef, useEffect } from "react";
import { useSetAtom } from "jotai";
import {
  Button,
  Text,
  TextInput,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { takeSnapshotSetter } from "@zapier/canvas-state";
import { isValidUrl, normalizeUrl } from "@zapier/rich-text-editor";
import useUpdateNode from "../../../../../hooks/useUpdateNode";
import { MenuWrapper } from "../../../../FloatingInlineToolbar/MenuWrapper";
import { EmbedExamples } from "../../../../FloatingInlineToolbar/FloatingLinkEmbed/EmbedExamples";
import styles from "./EmbedNodeLinkInput.module.css";

type Props = {
  link?: string;
  onClose: () => void;
  onCreate?: (link: string) => void; // Optional custom create handler
};

export function EmbedNodeLinkInput({ link, onClose, onCreate }: Props) {
  const [currentLink, setCurrentLink] = useState<string | undefined>(link);
  const [isError, setIsError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();

  // Auto-focus and optionally auto-select text
  useEffect(() => {
    if (inputRef.current) {
      // Small delay to ensure DOM is ready before focusing and selecting
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
        // Auto-select all text when editing existing link
        if (link) {
          inputRef.current?.select();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [link]);

  const validateAndSave = useCallback(() => {
    if (!currentLink?.trim()) {
      return;
    }

    const trimmed = currentLink.trim();
    const normalizedLink = normalizeUrl(trimmed);
    const isValid = isValidUrl(normalizedLink, { allowMedia: true });
    setIsError(!isValid);

    if (isValid && normalizedLink !== link) {
      takeSnapshot();

      if (onCreate) {
        // Use custom create handler if provided (for ephemeral nodes)
        onCreate(normalizedLink);
      } else {
        // Use default update behavior (for regular embed nodes)
        onClose();
        updateNodeAndSave(
          {
            data: { link: normalizedLink },
            // Reset dimensions when link changes to allow new content to size naturally
            width: undefined,
            height: undefined,
            measured: undefined,
          },
          { shouldSave: false },
        ); // Don't save since EmbedNodeContent will save once we have measured width and height
      }
    }
  }, [currentLink, link, takeSnapshot, updateNodeAndSave, onClose, onCreate]);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setCurrentLink(newValue);
      if (isError) {
        setIsError(false);
      }
    },
    [isError],
  );

  const handleRemoveLink = useCallback(() => {
    setCurrentLink("");
    setIsError(false);
    takeSnapshot();
    inputRef?.current?.select();
    updateNodeAndSave({
      data: { link: undefined },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && currentLink?.trim() && currentLink !== link) {
        e.preventDefault();
        validateAndSave();
      } else if (e.key === "Escape" && link) {
        // Only allow escape when there's an existing link to return to
        e.preventDefault();
        onClose();
      }
    },
    [currentLink, link, validateAndSave, onClose],
  );

  // Detect if there are unsaved changes
  const hasChanges = currentLink !== link;

  return (
    <MenuWrapper>
      <div className={styles.floatingTypeaheadWrapper}>
        <div className={styles.header}>
          <Text type="Body3" color="TextInverted">
            Embed link
          </Text>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="formX" color="TextInverted" />
          </button>
        </div>
        <Text type="MinimalPrint1" color="TextInverted">
          Paste a link to a third-party app, dashboard, document, or resource.
          Only publicly accessible or embeddable links will have a preview.
        </Text>
        {/* nopan className prevents zooming when you triple click to select all text in the input */}
        <TextInput
          ref={inputRef}
          className="nopan"
          name="url"
          placeholder="Paste link here"
          inputMode="url"
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          value={currentLink ?? ""}
          isErrored={isError}
          renderIconAfter={(iconProps) =>
            currentLink ? (
              <Tooltip placement="left" content="Remove link">
                <TooltipFocusableTrigger>
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLink();
                    }}
                    aria-label="Clear input"
                  >
                    <Icon
                      {...iconProps}
                      name="formXCircle"
                      size={iconProps.iconSize}
                    />
                  </button>
                </TooltipFocusableTrigger>
              </Tooltip>
            ) : null
          }
        />
        {isError && (
          <div className={styles.errorContainer}>
            <Text color="StatusError" type="MinimalPrint1Multiline">
              This doesn&apos;t look like a valid URL. Check the link and try
              again.
            </Text>
          </div>
        )}
        <div className={styles.buttonRow}>
          <Button
            size="compact"
            variant="primary"
            onPress={validateAndSave}
            isDisabled={!currentLink?.trim() || !hasChanges}
          >
            {link ? "Update" : "Add"}
          </Button>
        </div>
        <EmbedExamples />
      </div>
    </MenuWrapper>
  );
}
